/**
 * TCX-7 — Training Domain-Match Policy — shared UI building blocks.
 *
 * Provider-aware / shop-configurable domain-match policy admin surface.
 * The server (schema + RPCs) is already built + deployed; this module
 * only calls the RPCs. It is consumed by two surfaces:
 *
 *   • `src/pages/TrainingDomainPolicyAdmin.tsx` — internal-admin
 *     (global + provider scope).
 *   • `src/components/training/TrainingDomainPolicyShopSettings.tsx`
 *     — shop-manager scope.
 *
 * Every mutation passes `p_actor` = the authenticated user id; the RPC
 * re-verifies actor === auth.uid() server-side.
 *
 * RPCs are cast to narrow local interfaces (mirroring the pattern in
 * `TrainingProvidersAdminPanel.tsx`).
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertTriangle,
  Check,
  Loader2,
  Plus,
  ShieldAlert,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────
// Scope / mode primitives
// ─────────────────────────────────────────────────────────────────────

export type PolicyScope = 'global' | 'provider' | 'shop';
export type PolicyMode = 'permissive' | 'strict' | 'allowlist';
export type MatchType = 'exact' | 'subdomain' | 'path_prefix';

export const POLICY_MODES: PolicyMode[] = ['permissive', 'strict', 'allowlist'];

export const MODE_LABELS: Record<PolicyMode, string> = {
  permissive: 'Permissive',
  strict: 'Strict',
  allowlist: 'Allowlist',
};

export const MODE_EXPLANATIONS: Record<PolicyMode, string> = {
  permissive:
    'Permissive — host mismatches are stored as low-confidence diagnostics only. They never independently verify training. Nothing is quarantined.',
  strict:
    'Strict — only the exact host of the launched training page is accepted. Evidence from any other host is quarantined.',
  allowlist:
    'Allowlist — only explicitly approved provider/shop domains (plus the launched host) are accepted. Evidence from any other host is quarantined.',
};

export function isElevatedMode(mode: PolicyMode): boolean {
  return mode === 'strict' || mode === 'allowlist';
}

// ─────────────────────────────────────────────────────────────────────
// Error mapping + host validation
// ─────────────────────────────────────────────────────────────────────

/** Maps SQLSTATE 42501 → friendly permission copy; otherwise echoes the
 *  operator-readable RAISE message. Never dumps a raw stack. */
export function safeRpcError(
  error: { message: string; code?: string } | null,
): string {
  if (!error) return 'Unknown error';
  if (error.code === '42501') {
    return 'You do not have permission to perform that action.';
  }
  const msg = (error.message ?? '').trim();
  return msg.length === 0 ? 'Action failed.' : msg;
}

/** Client-side sanity check — the RPC is the real authority and will
 *  reject invalid / wildcard hosts, but we pre-flight to give a friendly
 *  message before submit. Accepts `sub.example.com`; rejects wildcards,
 *  schemes, paths, ports, and bare single-label hosts. */
export function isValidHost(raw: string): boolean {
  const host = raw.trim().toLowerCase();
  if (host.length === 0 || host.length > 253) return false;
  if (host.includes('*')) return false;
  if (/[/:\\?#@ ]/.test(host)) return false;
  // require at least two dot-separated labels (sub.example.com / example.com)
  const labels = host.split('.');
  if (labels.length < 2) return false;
  return labels.every((l) => /^(?!-)[a-z0-9-]{1,63}(?<!-)$/.test(l));
}

// ─────────────────────────────────────────────────────────────────────
// Narrow RPC casts (generated types exist, but we mirror the
// TrainingProvidersAdminPanel narrow-cast convention).
// ─────────────────────────────────────────────────────────────────────

interface RpcResult<T> {
  data: T;
  error: { message: string; code?: string } | null;
}

interface ResolvePolicyRpc {
  (
    fn: 'resolve_training_domain_policy',
    args: { p_shop_id: string | null; p_provider_id: string | null; p_artifact_id: string | null },
  ): Promise<RpcResult<ResolvedPolicy | null>>;
}

interface SetPolicyRpc {
  (
    fn: 'set_training_domain_policy',
    args: {
      p_scope: PolicyScope;
      p_provider_id: string | null;
      p_shop_id: string | null;
      p_mode: PolicyMode;
      p_enabled: boolean;
      p_note: string | null;
      p_actor: string;
    },
  ): Promise<RpcResult<{ ok: boolean; id: string } | null>>;
}

interface UpsertRuleRpc {
  (
    fn: 'upsert_training_domain_rule',
    args: {
      p_scope: PolicyScope;
      p_provider_id: string | null;
      p_shop_id: string | null;
      p_artifact_id: string | null;
      p_host: string;
      p_match_type: MatchType;
      p_path_prefix: string | null;
      p_enabled: boolean;
      p_source: string;
      p_note: string | null;
      p_actor: string;
    },
  ): Promise<RpcResult<{ ok: boolean; id: string } | null>>;
}

interface SetRuleEnabledRpc {
  (
    fn: 'set_training_domain_rule_enabled',
    args: { p_rule_id: string; p_enabled: boolean; p_actor: string },
  ): Promise<RpcResult<{ ok: boolean } | null>>;
}

interface ApproveCandidateRpc {
  (
    fn: 'approve_training_domain_rule_candidate',
    args: { p_candidate_id: string; p_match_type: MatchType; p_note: string | null; p_actor: string },
  ): Promise<RpcResult<{ ok: boolean; rule_id: string } | null>>;
}

interface RejectCandidateRpc {
  (
    fn: 'reject_training_domain_rule_candidate',
    args: { p_candidate_id: string; p_note: string | null; p_actor: string },
  ): Promise<RpcResult<{ ok: boolean } | null>>;
}

interface GenerateCandidatesRpc {
  (
    fn: 'generate_training_domain_rule_candidates',
    args: {
      p_shop_id: string | null;
      p_from: string;
      p_to: string;
      p_min_count: number;
      p_actor: string;
    },
  ): Promise<RpcResult<{ ok: boolean; candidates_upserted: number } | null>>;
}

interface DiagnosticsRpc {
  (
    fn: 'get_training_domain_mismatch_diagnostics_scoped',
    args: {
      p_shop_id: string | null;
      p_artifact_id: string | null;
      p_from: string;
      p_to: string;
      p_min_count: number;
    },
  ): Promise<RpcResult<DiagnosticsRow[] | null>>;
}

// ─────────────────────────────────────────────────────────────────────
// Row shapes
// ─────────────────────────────────────────────────────────────────────

export interface ResolvedRule {
  id: string;
  host: string;
  match_type: string;
  path_prefix: string | null;
  scope: string;
  source: string;
}

export interface ResolvedPolicy {
  mode: PolicyMode;
  policy_version: number;
  source_scope: string;
  rules: ResolvedRule[];
}

export interface RuleRow {
  id: string;
  host: string;
  match_type: string;
  path_prefix: string | null;
  scope: string;
  source: string;
  enabled: boolean;
  note: string | null;
  updated_at: string;
}

export interface CandidateRow {
  id: string;
  candidate_host: string;
  scope: string;
  shop_id: string | null;
  provider_id: string | null;
  artifact_id: string | null;
  mismatch_count: number;
  unique_sessions: number;
  first_seen: string | null;
  last_seen: string | null;
  status: string;
}

export interface DiagnosticsRow {
  shop_id: string;
  artifact_id: string;
  observed_host: string;
  opened_host: string;
  mismatch_count: number;
  unique_sessions: number;
  first_seen: string;
  last_seen: string;
}

export interface AuditRow {
  id: string;
  action: string;
  scope: string | null;
  provider_id: string | null;
  shop_id: string | null;
  artifact_id: string | null;
  reason: string | null;
  created_at: string;
  actor_user_id: string | null;
}

// A scope context identifies which policy/rules bucket a section acts on.
export interface ScopeCtx {
  scope: PolicyScope;
  providerId: string | null;
  shopId: string | null;
}

function scopeKey(ctx: ScopeCtx): string {
  return `${ctx.scope}:${ctx.providerId ?? '-'}:${ctx.shopId ?? '-'}`;
}

function fmtDate(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
}

function defaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

// ═════════════════════════════════════════════════════════════════════
// Shared informational blocks
// ═════════════════════════════════════════════════════════════════════

export function PolicyDisclaimer() {
  return (
    <Alert data-testid="tdp-disclaimer">
      <ShieldAlert className="h-4 w-4" />
      <AlertTitle>How domain-match policy is applied</AlertTitle>
      <AlertDescription className="space-y-2 text-sm">
        <p>
          Mismatched evidence <strong>never independently verifies training</strong>.
          A host mismatch can only ever lower confidence or quarantine
          evidence — it can never approve a completion on its own.
        </p>
        <p>
          Policy changes affect <strong>future evidence only</strong>. Historical
          verification decisions are never retroactively changed.
        </p>
      </AlertDescription>
    </Alert>
  );
}

export function ModeExplainer() {
  return (
    <div className="grid gap-2 text-xs text-muted-foreground" data-testid="tdp-mode-explainer">
      {POLICY_MODES.map((m) => (
        <div key={m} className="flex gap-2">
          <Badge variant="outline" className="h-5 shrink-0">
            {MODE_LABELS[m]}
          </Badge>
          <span>{MODE_EXPLANATIONS[m]}</span>
        </div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Effective policy card (read) + set-mode control
// ═════════════════════════════════════════════════════════════════════

interface EffectivePolicySectionProps {
  title: string;
  description?: string;
  /** Resolve inputs — pass what's relevant; nulls resolve inherited. */
  resolveShopId: string | null;
  resolveProviderId: string | null;
  resolveArtifactId: string | null;
  /** The scope this control WRITES to when setting a mode override. */
  writeCtx: ScopeCtx;
  actor: string | null;
  canWrite: boolean;
}

export function EffectivePolicySection({
  title,
  description,
  resolveShopId,
  resolveProviderId,
  resolveArtifactId,
  writeCtx,
  actor,
  canWrite,
}: EffectivePolicySectionProps) {
  const queryClient = useQueryClient();
  const resolveKey = [
    'tdp-resolve',
    resolveShopId ?? '-',
    resolveProviderId ?? '-',
    resolveArtifactId ?? '-',
  ];

  const resolveQuery = useQuery({
    queryKey: resolveKey,
    queryFn: async (): Promise<ResolvedPolicy | null> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ResolvePolicyRpc;
      const { data, error } = await rpc('resolve_training_domain_policy', {
        p_shop_id: resolveShopId,
        p_provider_id: resolveProviderId,
        p_artifact_id: resolveArtifactId,
      });
      if (error) throw new Error(safeRpcError(error));
      return data ?? null;
    },
  });

  const effective = resolveQuery.data;

  const [pendingMode, setPendingMode] = useState<PolicyMode>('permissive');
  const [note, setNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const setModeMutation = useMutation({
    mutationFn: async (args: { mode: PolicyMode; note: string }) => {
      if (!actor) throw new Error('Sign in required.');
      const rpc = supabase.rpc.bind(supabase) as unknown as SetPolicyRpc;
      const { error } = await rpc('set_training_domain_policy', {
        p_scope: writeCtx.scope,
        p_provider_id: writeCtx.providerId,
        p_shop_id: writeCtx.shopId,
        p_mode: args.mode,
        p_enabled: true,
        p_note: args.note.trim() || null,
        p_actor: actor,
      });
      if (error) throw new Error(safeRpcError(error));
    },
    onSuccess: () => {
      toast.success('Policy mode updated. Applies to future evidence only.');
      setConfirmOpen(false);
      setNote('');
      queryClient.invalidateQueries({ queryKey: ['tdp-resolve'] });
      queryClient.invalidateQueries({ queryKey: ['tdp-audit'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const submitMode = () => {
    if (isElevatedMode(pendingMode)) {
      setConfirmOpen(true);
    } else {
      setModeMutation.mutate({ mode: pendingMode, note });
    }
  };

  return (
    <Card data-testid="tdp-effective-policy">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        {resolveQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Resolving effective policy…
          </div>
        ) : resolveQuery.error ? (
          <p className="text-sm text-muted-foreground">
            {(resolveQuery.error as Error).message}
          </p>
        ) : effective ? (
          <div className="flex flex-wrap items-center gap-3" data-testid="tdp-effective-summary">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Effective mode:</span>
              <Badge
                variant={isElevatedMode(effective.mode) ? 'destructive' : 'default'}
                data-testid="tdp-effective-mode"
              >
                {MODE_LABELS[effective.mode] ?? effective.mode}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Inherited from:</span>
              <Badge variant="secondary" data-testid="tdp-effective-source-scope">
                {effective.source_scope}
              </Badge>
            </div>
            <span className="text-xs text-muted-foreground">
              policy v{effective.policy_version} · {effective.rules?.length ?? 0} effective rule(s)
            </span>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No effective policy resolved.</p>
        )}

        <ModeExplainer />

        {canWrite && (
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">
              Set {writeCtx.scope} mode override
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Mode</Label>
                <Select
                  value={pendingMode}
                  onValueChange={(v) => setPendingMode(v as PolicyMode)}
                >
                  <SelectTrigger className="w-[180px]" data-testid="tdp-set-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POLICY_MODES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {MODE_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px] space-y-1">
                <Label className="text-xs">Note (optional)</Label>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Reason for this change"
                  data-testid="tdp-set-mode-note"
                />
              </div>
              <Button
                onClick={submitMode}
                disabled={setModeMutation.isPending}
                data-testid="tdp-set-mode-submit"
              >
                {setModeMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                )}
                Apply mode
              </Button>
            </div>
            {isElevatedMode(pendingMode) && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {MODE_LABELS[pendingMode]} quarantines non-matching evidence.
                Review diagnostics first.
              </p>
            )}
          </div>
        )}
      </CardContent>

      {/* Elevated-mode confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="tdp-elevated-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Enable {MODE_LABELS[pendingMode]} mode?
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block">
                {MODE_EXPLANATIONS[pendingMode]}
              </span>
              <span className="block">
                This risks quarantining legitimate training flows whose host is
                not yet approved. We recommend reviewing the mismatch
                diagnostics and approving expected domains first. This change
                affects future evidence only.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={setModeMutation.isPending}
              data-testid="tdp-elevated-warning-confirm"
              onClick={() => setModeMutation.mutate({ mode: pendingMode, note })}
            >
              {setModeMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              Enable {MODE_LABELS[pendingMode]}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Rules manager (add / disable / edit-note)
// ═════════════════════════════════════════════════════════════════════

interface RulesManagerProps {
  ctx: ScopeCtx;
  actor: string | null;
  canWrite: boolean;
  title?: string;
}

export function RulesManager({ ctx, actor, canWrite, title }: RulesManagerProps) {
  const queryClient = useQueryClient();
  const key = ['tdp-rules', scopeKey(ctx)];

  const rulesQuery = useQuery({
    queryKey: key,
    queryFn: async (): Promise<RuleRow[]> => {
      let q = supabase
        .from('training_domain_rule')
        .select('id,host,match_type,path_prefix,scope,source,enabled,note,updated_at')
        .eq('scope', ctx.scope)
        .order('host', { ascending: true });
      if (ctx.scope === 'provider') q = q.eq('provider_id', ctx.providerId ?? '');
      if (ctx.scope === 'shop') q = q.eq('shop_id', ctx.shopId ?? '');
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as RuleRow[];
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ['tdp-resolve'] });
    queryClient.invalidateQueries({ queryKey: ['tdp-audit'] });
  };

  // Add / edit-note dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RuleRow | null>(null);
  const [host, setHost] = useState('');
  const [matchType, setMatchType] = useState<MatchType>('exact');
  const [pathPrefix, setPathPrefix] = useState('');
  const [note, setNote] = useState('');

  const openAdd = () => {
    setEditing(null);
    setHost('');
    setMatchType('exact');
    setPathPrefix('');
    setNote('');
    setDialogOpen(true);
  };
  const openEdit = (r: RuleRow) => {
    setEditing(r);
    setHost(r.host);
    setMatchType((r.match_type as MatchType) ?? 'exact');
    setPathPrefix(r.path_prefix ?? '');
    setNote(r.note ?? '');
    setDialogOpen(true);
  };

  const upsertMutation = useMutation({
    mutationFn: async () => {
      if (!actor) throw new Error('Sign in required.');
      if (!isValidHost(host)) {
        throw new Error('Enter a valid host like sub.example.com (no wildcards, scheme, or path).');
      }
      if (matchType === 'path_prefix' && pathPrefix.trim().length === 0) {
        throw new Error('Path-prefix rules require a path prefix (e.g. /courses/).');
      }
      const rpc = supabase.rpc.bind(supabase) as unknown as UpsertRuleRpc;
      const { error } = await rpc('upsert_training_domain_rule', {
        p_scope: ctx.scope,
        p_provider_id: ctx.providerId,
        p_shop_id: ctx.shopId,
        p_artifact_id: null,
        p_host: host.trim().toLowerCase(),
        p_match_type: matchType,
        p_path_prefix: matchType === 'path_prefix' ? pathPrefix.trim() : null,
        p_enabled: true,
        p_source: 'manual',
        p_note: note.trim() || null,
        p_actor: actor,
      });
      if (error) throw new Error(safeRpcError(error));
    },
    onSuccess: () => {
      toast.success(editing ? 'Rule updated.' : 'Approved domain added.');
      setDialogOpen(false);
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const toggleMutation = useMutation({
    mutationFn: async (args: { ruleId: string; enabled: boolean }) => {
      if (!actor) throw new Error('Sign in required.');
      const rpc = supabase.rpc.bind(supabase) as unknown as SetRuleEnabledRpc;
      const { error } = await rpc('set_training_domain_rule_enabled', {
        p_rule_id: args.ruleId,
        p_enabled: args.enabled,
        p_actor: actor,
      });
      if (error) throw new Error(safeRpcError(error));
    },
    onSuccess: (_d, vars) => {
      toast.success(vars.enabled ? 'Rule enabled.' : 'Rule disabled.');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const rules = rulesQuery.data ?? [];

  return (
    <Card data-testid="tdp-rules-manager">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">{title ?? 'Approved domain rules'}</CardTitle>
          <CardDescription>
            Approved hosts for {ctx.scope} scope. Disabling a rule is the way to
            remove it from future matching.
          </CardDescription>
        </div>
        {canWrite && (
          <Button size="sm" onClick={openAdd} data-testid="tdp-rule-add">
            <Plus className="h-4 w-4 mr-1" />
            Add domain
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {rulesQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading rules…
          </div>
        ) : rulesQuery.error ? (
          <p className="text-sm text-muted-foreground py-4">
            {(rulesQuery.error as Error).message}
          </p>
        ) : rules.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No rules configured for this scope.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Host</TableHead>
                  <TableHead>Match</TableHead>
                  <TableHead>Path prefix</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((r) => (
                  <TableRow key={r.id} data-testid="tdp-rule-row">
                    <TableCell className="font-mono text-xs">{r.host}</TableCell>
                    <TableCell className="text-xs">{r.match_type}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.path_prefix ?? '—'}
                    </TableCell>
                    <TableCell className="text-xs">{r.source}</TableCell>
                    <TableCell>
                      <Badge variant={r.enabled ? 'default' : 'outline'} className="text-xs">
                        {r.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {canWrite && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(r)}
                            data-testid="tdp-rule-edit-note"
                          >
                            Edit note
                          </Button>
                          <Button
                            size="sm"
                            variant={r.enabled ? 'outline' : 'default'}
                            disabled={toggleMutation.isPending}
                            onClick={() =>
                              toggleMutation.mutate({ ruleId: r.id, enabled: !r.enabled })
                            }
                            data-testid="tdp-rule-toggle"
                          >
                            {r.enabled ? 'Disable' : 'Enable'}
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="tdp-rule-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit rule' : 'Add approved domain'}</DialogTitle>
            <DialogDescription>
              Enter a host like <code>academy.example.com</code>. Wildcards are
              rejected — choose <em>subdomain</em> match to cover subdomains.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Host</Label>
              <Input
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="sub.example.com"
                data-testid="tdp-rule-host-input"
                autoComplete="off"
              />
              {host.length > 0 && !isValidHost(host) && (
                <p className="text-xs text-destructive">
                  Not a valid host. Example: sub.example.com
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Match type</Label>
              <Select value={matchType} onValueChange={(v) => setMatchType(v as MatchType)}>
                <SelectTrigger data-testid="tdp-rule-match-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">Exact host</SelectItem>
                  <SelectItem value="subdomain">Subdomain (host + any subdomain)</SelectItem>
                  <SelectItem value="path_prefix">Path prefix</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {matchType === 'path_prefix' && (
              <div className="space-y-1">
                <Label className="text-xs">Path prefix</Label>
                <Input
                  value={pathPrefix}
                  onChange={(e) => setPathPrefix(e.target.value)}
                  placeholder="/courses/"
                  data-testid="tdp-rule-path-prefix"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Note (optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why this domain is approved"
                data-testid="tdp-rule-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={upsertMutation.isPending || !isValidHost(host)}
              onClick={() => upsertMutation.mutate()}
              data-testid="tdp-rule-submit"
            >
              {upsertMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              {editing ? 'Save' : 'Add domain'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Candidates manager (approve / reject, optional generate)
// ═════════════════════════════════════════════════════════════════════

interface CandidatesManagerProps {
  /** Shop scope filter — null shows all pending (internal admin). */
  shopId: string | null;
  actor: string | null;
  canWrite: boolean;
  /** Show the generate-from-diagnostics control (shop surface). */
  showGenerate?: boolean;
}

export function CandidatesManager({
  shopId,
  actor,
  canWrite,
  showGenerate,
}: CandidatesManagerProps) {
  const queryClient = useQueryClient();
  const key = ['tdp-candidates', shopId ?? 'all'];

  const candQuery = useQuery({
    queryKey: key,
    queryFn: async (): Promise<CandidateRow[]> => {
      let q = supabase
        .from('training_domain_rule_candidate')
        .select(
          'id,candidate_host,scope,shop_id,provider_id,artifact_id,mismatch_count,unique_sessions,first_seen,last_seen,status',
        )
        .eq('status', 'pending')
        .order('mismatch_count', { ascending: false });
      if (shopId) q = q.eq('shop_id', shopId);
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as CandidateRow[];
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ['tdp-rules'] });
    queryClient.invalidateQueries({ queryKey: ['tdp-resolve'] });
    queryClient.invalidateQueries({ queryKey: ['tdp-audit'] });
  };

  // Approve dialog
  const [approveTarget, setApproveTarget] = useState<CandidateRow | null>(null);
  const [approveMatch, setApproveMatch] = useState<MatchType>('exact');
  const [approveNote, setApproveNote] = useState('');

  // Reject dialog
  const [rejectTarget, setRejectTarget] = useState<CandidateRow | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!actor || !approveTarget) throw new Error('Sign in required.');
      const rpc = supabase.rpc.bind(supabase) as unknown as ApproveCandidateRpc;
      const { error } = await rpc('approve_training_domain_rule_candidate', {
        p_candidate_id: approveTarget.id,
        p_match_type: approveMatch,
        p_note: approveNote.trim() || null,
        p_actor: actor,
      });
      if (error) throw new Error(safeRpcError(error));
    },
    onSuccess: () => {
      toast.success('Candidate approved into a domain rule.');
      setApproveTarget(null);
      setApproveNote('');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      if (!actor || !rejectTarget) throw new Error('Sign in required.');
      const rpc = supabase.rpc.bind(supabase) as unknown as RejectCandidateRpc;
      const { error } = await rpc('reject_training_domain_rule_candidate', {
        p_candidate_id: rejectTarget.id,
        p_note: rejectNote.trim() || null,
        p_actor: actor,
      });
      if (error) throw new Error(safeRpcError(error));
    },
    onSuccess: () => {
      toast.success('Candidate rejected.');
      setRejectTarget(null);
      setRejectNote('');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Generate control
  const range = useMemo(defaultRange, []);
  const [genFrom, setGenFrom] = useState(range.from);
  const [genTo, setGenTo] = useState(range.to);
  const [genMin, setGenMin] = useState(3);

  const generateMutation = useMutation({
    mutationFn: async (): Promise<number> => {
      if (!actor) throw new Error('Sign in required.');
      const rpc = supabase.rpc.bind(supabase) as unknown as GenerateCandidatesRpc;
      const { data, error } = await rpc('generate_training_domain_rule_candidates', {
        p_shop_id: shopId,
        p_from: new Date(genFrom).toISOString(),
        p_to: new Date(genTo).toISOString(),
        p_min_count: genMin,
        p_actor: actor,
      });
      if (error) throw new Error(safeRpcError(error));
      return data?.candidates_upserted ?? 0;
    },
    onSuccess: (n) => {
      toast.success(`${n} candidate(s) generated from diagnostics.`);
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const candidates = candQuery.data ?? [];

  return (
    <Card data-testid="tdp-candidates-manager">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Pending domain candidates</CardTitle>
        <CardDescription>
          Observed hosts that recur across mismatches. Approve to create an
          approved-domain rule (future evidence only), or reject.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {showGenerate && canWrite && (
          <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={genFrom}
                onChange={(e) => setGenFrom(e.target.value)}
                data-testid="tdp-generate-from"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={genTo}
                onChange={(e) => setGenTo(e.target.value)}
                data-testid="tdp-generate-to"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Min count</Label>
              <Input
                type="number"
                min={1}
                value={genMin}
                onChange={(e) => setGenMin(Math.max(1, Number(e.target.value) || 1))}
                className="w-24"
                data-testid="tdp-generate-min"
              />
            </div>
            <Button
              variant="outline"
              disabled={generateMutation.isPending}
              onClick={() => generateMutation.mutate()}
              data-testid="tdp-generate-submit"
            >
              {generateMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              Generate candidates
            </Button>
          </div>
        )}

        {candQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading candidates…
          </div>
        ) : candQuery.error ? (
          <p className="text-sm text-muted-foreground py-4">
            {(candQuery.error as Error).message}
          </p>
        ) : candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No pending candidates.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Host</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead className="text-right">Mismatches</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.map((c) => (
                  <TableRow key={c.id} data-testid="tdp-candidate-row">
                    <TableCell className="font-mono text-xs">{c.candidate_host}</TableCell>
                    <TableCell className="text-xs">{c.scope}</TableCell>
                    <TableCell className="text-right text-xs">{c.mismatch_count}</TableCell>
                    <TableCell className="text-right text-xs">{c.unique_sessions}</TableCell>
                    <TableCell className="text-xs">{fmtDate(c.last_seen)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {canWrite && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => {
                              setApproveTarget(c);
                              setApproveMatch('exact');
                              setApproveNote('');
                            }}
                            data-testid="tdp-candidate-approve"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setRejectTarget(c);
                              setRejectNote('');
                            }}
                            data-testid="tdp-candidate-reject"
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Approve dialog */}
      <Dialog open={approveTarget !== null} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <DialogContent data-testid="tdp-approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve domain candidate</DialogTitle>
            <DialogDescription>
              {approveTarget && (
                <span className="font-mono">{approveTarget.candidate_host}</span>
              )}
              {' '}will become an approved-domain rule. Applies to future evidence
              only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Match type</Label>
              <Select value={approveMatch} onValueChange={(v) => setApproveMatch(v as MatchType)}>
                <SelectTrigger data-testid="tdp-approve-match-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">Exact host</SelectItem>
                  <SelectItem value="subdomain">Subdomain</SelectItem>
                  <SelectItem value="path_prefix">Path prefix</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Note (optional)</Label>
              <Textarea
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                data-testid="tdp-approve-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)}>
              Cancel
            </Button>
            <Button
              disabled={approveMutation.isPending}
              onClick={() => approveMutation.mutate()}
              data-testid="tdp-approve-confirm"
            >
              {approveMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectTarget !== null} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent data-testid="tdp-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject domain candidate</DialogTitle>
            <DialogDescription>
              {rejectTarget && (
                <span className="font-mono">{rejectTarget.candidate_host}</span>
              )}
              {' '}will be dismissed and not turned into a rule.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label className="text-xs">Note (optional)</Label>
            <Textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              data-testid="tdp-reject-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={rejectMutation.isPending}
              onClick={() => rejectMutation.mutate()}
              data-testid="tdp-reject-confirm"
            >
              {rejectMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Mismatch diagnostics
// ═════════════════════════════════════════════════════════════════════

interface DiagnosticsSectionProps {
  /** null = all shops (internal admin). */
  shopId: string | null;
  description?: string;
}

export function DiagnosticsSection({ shopId, description }: DiagnosticsSectionProps) {
  const range = useMemo(defaultRange, []);
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [minCount, setMinCount] = useState(1);

  const key = ['tdp-diagnostics', shopId ?? 'all', from, to, minCount];
  const diagQuery = useQuery({
    queryKey: key,
    queryFn: async (): Promise<DiagnosticsRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as DiagnosticsRpc;
      const { data, error } = await rpc(
        'get_training_domain_mismatch_diagnostics_scoped',
        {
          p_shop_id: shopId,
          p_artifact_id: null,
          p_from: new Date(from).toISOString(),
          p_to: new Date(to).toISOString(),
          p_min_count: minCount,
        },
      );
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  const rows = diagQuery.data ?? [];

  return (
    <Card data-testid="tdp-diagnostics">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Mismatch diagnostics</CardTitle>
        <CardDescription>
          {description ??
            'Observed host vs. launched host, for evidence that did not match. Diagnostics never independently verify training.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="tdp-diag-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="tdp-diag-to" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Min count</Label>
            <Input
              type="number"
              min={1}
              value={minCount}
              onChange={(e) => setMinCount(Math.max(1, Number(e.target.value) || 1))}
              className="w-24"
              data-testid="tdp-diag-min"
            />
          </div>
        </div>

        {diagQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading diagnostics…
          </div>
        ) : diagQuery.error ? (
          <p className="text-sm text-muted-foreground py-4">
            {(diagQuery.error as Error).message}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No mismatches in this window.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Observed host</TableHead>
                  <TableHead>Launched host</TableHead>
                  <TableHead className="text-right">Mismatches</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                  <TableHead>First seen</TableHead>
                  <TableHead>Last seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.observed_host}-${r.opened_host}-${i}`} data-testid="tdp-diag-row">
                    <TableCell className="font-mono text-xs">{r.observed_host}</TableCell>
                    <TableCell className="font-mono text-xs">{r.opened_host}</TableCell>
                    <TableCell className="text-right text-xs">{r.mismatch_count}</TableCell>
                    <TableCell className="text-right text-xs">{r.unique_sessions}</TableCell>
                    <TableCell className="text-xs">{fmtDate(r.first_seen)}</TableCell>
                    <TableCell className="text-xs">{fmtDate(r.last_seen)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Audit history (read-only)
// ═════════════════════════════════════════════════════════════════════

interface AuditSectionProps {
  /** null = unfiltered (internal admin). */
  shopId: string | null;
}

export function AuditSection({ shopId }: AuditSectionProps) {
  const key = ['tdp-audit', shopId ?? 'all'];
  const auditQuery = useQuery({
    queryKey: key,
    queryFn: async (): Promise<AuditRow[]> => {
      let q = supabase
        .from('training_domain_policy_audit')
        .select('id,action,scope,provider_id,shop_id,artifact_id,reason,created_at,actor_user_id')
        .order('created_at', { ascending: false })
        .limit(100);
      if (shopId) q = q.eq('shop_id', shopId);
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as AuditRow[];
    },
  });

  const rows = auditQuery.data ?? [];

  return (
    <Card data-testid="tdp-audit">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Policy audit history</CardTitle>
        <CardDescription>
          Most recent policy and rule changes (newest first).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {auditQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading audit history…
          </div>
        ) : auditQuery.error ? (
          <p className="text-sm text-muted-foreground py-4">
            {(auditQuery.error as Error).message}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No audit entries yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid="tdp-audit-row">
                    <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-xs">{r.action}</TableCell>
                    <TableCell className="text-xs">{r.scope ?? '—'}</TableCell>
                    <TableCell className="text-xs">{r.reason ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
